import axios from 'axios'
import { getIdToken, getIdTokenForUid } from '@/utils/auth'
import { API_BASE } from '@/services/api'
import type { BankAccount } from '@/services/userData'

/** Call backend to get Plaid link token */
export type PlaidLinkMode = 'initial' | 'update'

/**
 * What the app intends to do with the public_token Link returns.
 *
 * Deliberately wider than PlaidLinkMode, which is Plaid's own idea of what kind
 * of Link session to open. Adding a bank opens an ordinary 'initial' session —
 * what differs is where the resulting token is sent afterwards, and sending it
 * to the wrong place is what replaces the user's existing bank. Keeping this
 * separate stops 'additional' leaking into the createLinkToken contract, where
 * Plaid would not know what to do with it.
 */
export type PlaidLinkIntent = PlaidLinkMode | 'additional' | 'relink'

/** The Link session mode to open for a given intent. */
export const linkModeForIntent = (intent: PlaidLinkIntent): PlaidLinkMode =>
  intent === 'update' ? 'update' : 'initial'

export interface PlaidLinkSession {
  linkToken: string
  mode: PlaidLinkIntent
  enableRefresh: boolean
  expiresAt: number
  returnPath: string
  /**
   * Which bank an update-mode session belongs to.
   *
   * An OAuth bank finishes Link after a full page redirect, so the choice the
   * user made before leaving has to survive in storage — otherwise the callback
   * completes the update against whichever connection resolves first. Absent for
   * a caller with one bank, and for initial mode.
   */
  itemId?: string
  /**
   * Who opened this session.
   *
   * Recorded at open time, when the user is certainly signed in, because the
   * callback cannot rely on React state: getIdToken waits on its own
   * onAuthStateChanged listener, so the request can succeed while the auth
   * context still reads null and the accounts get staged under the anonymous
   * key. On a shared browser the same gap could stage them under whichever
   * account resolved instead — this pins them to the one that started the flow.
   */
  uid?: string
  /** Bind an onboarding repair's return to the run that opened Link. */
  onboardingGeneration?: number
  /**
   * Whether the returning callback should stage every connected account rather
   * than only the bank it just added.
   *
   * `pendingBankAccounts` is a queue of banks still to walk, not a list of banks
   * the user has. The two screens that add a bank need opposite things from it.
   * ConnectNewBank runs after every existing bank has been through its steps, so
   * staging the new one alone is right. ConnectBankIntro runs before any of them
   * have — a returning user's existing bank is still waiting — so staging the new
   * one alone silently drops it from the run while it stays connected at Plaid.
   *
   * The in-page path merges the two lists itself. This one cannot: /oauth/callback
   * renders outside the V1 provider after a full page reload, with no access to
   * what was staged. Re-reading every account after the Item is stored gives the
   * same set, which is why this is a flag rather than a payload.
   */
  stageAllOnReturn?: boolean
  /**
   * Whether the exchange that finishes this session must refuse to replace an
   * existing connection.
   *
   * Carried through storage because an OAuth bank finishes in the callback after
   * a full page redirect, and nothing else there distinguishes a V1 initial
   * session from a V0 one — both record mode 'initial'. Without this the callback
   * used the permissive exchange for every OAuth bank, and a returning V1 user
   * could still have their connection replaced.
   *
   * Absent means the old behaviour, which is what V0 needs: it re-exchanges on
   * purpose to renew a lapsed 25-minute session.
   */
  requireNoExistingConnection?: boolean
}

/**
 * An Item id, or nothing.
 *
 * The update-mode entry points are passed straight to buttons as onClick, so
 * React calls them with a synthetic event where an id is expected. Their props
 * are typed `() => void`, which accepts a wider signature, so the compiler never
 * objects — and the event would travel into the request body and be rejected by
 * the backend's string schema, breaking ordinary single-bank reconnects.
 */
export const asItemId = (value: unknown): string | undefined =>
  typeof value === 'string' && value.length > 0 ? value : undefined

const PLAID_LINK_SESSION_KEY = 'plaid_link_session'

export const savePlaidLinkSession = (session: PlaidLinkSession): void => {
  sessionStorage.setItem(PLAID_LINK_SESSION_KEY, JSON.stringify(session))
}

export const getPlaidLinkSession = (): PlaidLinkSession | null => {
  const raw = sessionStorage.getItem(PLAID_LINK_SESSION_KEY)
  if (!raw) return null
  try {
    const session = JSON.parse(raw) as PlaidLinkSession
    if (!session.linkToken || session.expiresAt <= Date.now()) {
      sessionStorage.removeItem(PLAID_LINK_SESSION_KEY)
      return null
    }
    return session
  } catch {
    sessionStorage.removeItem(PLAID_LINK_SESSION_KEY)
    return null
  }
}

export const clearPlaidLinkSession = (): void => {
  sessionStorage.removeItem(PLAID_LINK_SESSION_KEY)
}

export const createPlaidLinkToken = async (
  mode: PlaidLinkMode = 'initial',
  accountSelectionEnabled: boolean = false,
  /** Which connection update mode should reuse; omit when the user has one. */
  itemId?: string,
  expectedUid?: string
): Promise<string> => {
  const token = expectedUid ? await getIdTokenForUid(expectedUid) : await getIdToken()
  const { data } = await axios.post(
    `${API_BASE}/plaidApi/v1/createLinkToken`,
    { mode, accountSelectionEnabled, ...(itemId && { itemId }) },
    { headers: { Authorization: `Bearer ${token}` } }
  )
  return data.link_token
}

/** One bank's accounts, as the backend reports them after a connection. */
export interface AddedPlaidItem {
  itemId: string
  accounts: BankAccount[]
}

/**
 * Why a relink left the bank it was replacing connected.
 *
 * Only 'institution_unverified' today: the backend could not establish that the
 * new connection is the same bank, so it declined to retire the old one. Kept
 * separate from cleanupFailed because the user is being told a different thing —
 * not "finish tidying up" but "we left this alone on purpose, check it".
 *
 * Mirrors OldItemKeptReasonSchema, deliberately rather than importing it: the
 * frontend's TS project cannot reach plaidRoutes.schema, which pulls in
 * userData.schema, and widening that boundary for one union is not worth it. The
 * coupling that can actually break is between the service and that schema, since
 * the schema parses the handler's result after the Item is stored — and those two
 * do share one definition. A reason added there but not here fails this value
 * check and shows nothing, which is the mild half of the problem.
 */
export type OldItemKeptReason = 'institution_unverified'

/**
 * Connect a bank alongside the ones the user already has.
 *
 * Separate from exchangePlaidPublicToken, which replaces the user's connection —
 * correct for their first bank, destructive for their second. The response names
 * the bank it just connected, so no caller has to work that out by comparing
 * account lists before and after.
 */
export const addPlaidItem = async (
  publicToken: string,
  enableRefresh: boolean = false,
  replacesItemId?: string,
  expectedUid?: string
): Promise<
  AddedPlaidItem & {
    cleanupFailed?: boolean
    pausedSyncPreferenceCount?: number
    oldItemKeptReason?: OldItemKeptReason
  }
> => {
  const token = expectedUid ? await getIdTokenForUid(expectedUid) : await getIdToken()
  const { data } = await axios.post<
    AddedPlaidItem & {
      cleanupFailed?: boolean
      pausedSyncPreferenceCount?: number
      oldItemKeptReason?: OldItemKeptReason
    }
  >(
    `${API_BASE}/plaidApi/v1/addPlaidItem`,
    { public_token: publicToken, enableRefresh, ...(replacesItemId && { replacesItemId }) },
    { headers: { Authorization: `Bearer ${token}` } }
  )
  // Every field the response may carry, named one by one.
  //
  // oldItemKeptReason was missing here while the signature above advertised it, and
  // a return type wider than the object satisfies the compiler — so the relink that
  // deliberately kept both banks reached neither render site, and the user was left
  // with two connections and nothing said about why.
  return {
    itemId: data.itemId,
    accounts: data.accounts ?? [],
    cleanupFailed: data.cleanupFailed,
    pausedSyncPreferenceCount: data.pausedSyncPreferenceCount,
    oldItemKeptReason: data.oldItemKeptReason,
  }
}

/**
 * Replace a credential Plaid can no longer use.
 *
 * The replacement is connected first. Removing first would leave the user with
 * no bank if they cancel Link or the exchange fails. A cleanup failure is
 * returned as a warning: the working replacement is still a successful result,
 * and the stale broken card can be removed on a later retry.
 */
export const relinkPlaidItem = async (
  publicToken: string,
  previousItemId: string,
  enableRefresh: boolean = false,
  expectedUid?: string
): Promise<
  AddedPlaidItem & {
    cleanupFailed: boolean
    pausedSyncPreferenceCount: number
    oldItemKeptReason?: OldItemKeptReason
  }
> => {
  const added = await addPlaidItem(publicToken, enableRefresh, previousItemId, expectedUid)
  return {
    ...added,
    cleanupFailed: added.cleanupFailed ?? false,
    pausedSyncPreferenceCount: added.pausedSyncPreferenceCount ?? 0,
    // Passed through only when present, so the absence of a reason stays absent
    // rather than becoming a reason of its own.
    ...(added.oldItemKeptReason ? { oldItemKeptReason: added.oldItemKeptReason } : {}),
  }
}

/** Refresh stored account metadata after Link update mode completes. */
export type CompletePlaidUpdateResult = {
  removedAccountCount: number
  removedSyncPreferenceCount: number
}

/** @param itemId which connection was updated; omit when the user has one. */
export const completePlaidUpdate = async (
  itemId?: string,
  expectedUid?: string
): Promise<CompletePlaidUpdateResult> => {
  const token = expectedUid ? await getIdTokenForUid(expectedUid) : await getIdToken()
  const { data } = await axios.post<CompletePlaidUpdateResult>(
    `${API_BASE}/plaidApi/v1/completeUpdate`,
    itemId ? { itemId } : {},
    { headers: { Authorization: `Bearer ${token}` } }
  )
  return {
    removedAccountCount: data.removedAccountCount,
    removedSyncPreferenceCount: data.removedSyncPreferenceCount,
  }
}

/**
 * Exchange public token from Plaid Link for access token (handled securely in backend).
 *
 * @param requireNoExistingConnection refuse the exchange if the user turns out to
 * have a bank already, instead of replacing it. For callers that only ever use
 * this to make a FIRST connection — the whole of V1. V0 leaves it off, because it
 * re-exchanges on purpose to renew a lapsed 25-minute session.
 */
export const exchangePlaidPublicToken = async (
  publicToken: string,
  enableRefresh: boolean = false,
  requireNoExistingConnection: boolean = false,
  expectedUid?: string
) => {
  const token = expectedUid ? await getIdTokenForUid(expectedUid) : await getIdToken()
  await axios.post(
    `${API_BASE}/plaidApi/v1/exchangeToken`,
    { public_token: publicToken, enableRefresh, requireNoExistingConnection },
    { headers: { Authorization: `Bearer ${token}` } }
  )
}

/** Revoke Plaid Item on Plaid's side and remove the stored access token from Firestore */
/** @param itemId which bank to disconnect; omit when the user has one. */
/** Whether Plaid access was already revoked when the request failed. */
export const wasPlaidAccessRevoked = (error: unknown): boolean =>
  axios.isAxiosError(error) && error.response?.data?.revoked === true

const DISCONNECT_TIMEOUT_MS = 20_000
const DISCONNECT_STATUS_TIMEOUT_MS = 1_000
const DISCONNECT_STATUS_ATTEMPTS = 4
const DISCONNECT_STATUS_DELAY_MS = 1_000

const wait = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms))

const isCancelledRequest = (error: unknown): boolean =>
  axios.isCancel(error) ||
  (axios.isAxiosError(error) && error.code === 'ERR_CANCELED') ||
  (error instanceof DOMException && ['AbortError', 'TimeoutError'].includes(error.name))

/**
 * Resolve the uncertain outcome created when only the browser stopped waiting.
 *
 * The backend retains an Item until revocation and every local cleanup write
 * commit. Once this endpoint says it is absent, callers can apply the same local
 * success path as a 200 response without guessing from an empty account list.
 */
async function waitForDisconnectCompletion(token: string, itemId?: string): Promise<boolean> {
  for (let attempt = 0; attempt < DISCONNECT_STATUS_ATTEMPTS; attempt += 1) {
    try {
      const { data } = await axios.post<{ complete: boolean }>(
        `${API_BASE}/plaidApi/v1/disconnectPlaidStatus`,
        itemId ? { itemId } : {},
        {
          headers: { Authorization: `Bearer ${token}` },
          signal: AbortSignal.timeout(DISCONNECT_STATUS_TIMEOUT_MS),
        }
      )
      if (data.complete) return true
    } catch {
      // A transient status failure says nothing about the original operation.
      // Keep checking within the bounded recovery window.
    }

    if (attempt < DISCONNECT_STATUS_ATTEMPTS - 1) {
      await wait(DISCONNECT_STATUS_DELAY_MS)
    }
  }
  return false
}

/**
 * Bounded, because a caller can be blocking on it. axios has no default timeout,
 * so a request that connects and is never answered leaves this pending forever —
 * and onboarding's removal dialog disables its own buttons while it waits, which
 * turned a hung request into a modal the user could only leave by reloading.
 */
export const disconnectPlaid = async (itemId?: string, signal?: AbortSignal): Promise<void> => {
  const token = await getIdToken()
  const usesBuiltInTimeout = signal === undefined

  try {
    await axios.post(`${API_BASE}/plaidApi/v1/disconnectPlaid`, itemId ? { itemId } : {}, {
      headers: { Authorization: `Bearer ${token}` },
      signal: signal ?? AbortSignal.timeout(DISCONNECT_TIMEOUT_MS),
    })
  } catch (error) {
    // A caller-supplied abort means the caller deliberately stopped the work and
    // should regain control immediately. Only our own timeout creates the
    // ambiguous "server may still be running" state this recovery resolves.
    if (!usesBuiltInTimeout || !isCancelledRequest(error)) throw error
    if (await waitForDisconnectCompletion(token, itemId)) return
    throw error
  }
}
